import React from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { Button, Container, Typography, Box, Paper } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';

// Report to Sentry without statically bundling the SDK — it's imported on demand
// only on the (rare) error path, so it stays out of the initial bundle.
const reportToSentry = (error) => {
  if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_SENTRY_DSN) {
    import('@sentry/react')
      .then((Sentry) => Sentry.captureException(error))
      .catch(() => {});
  }
};

// Error fallback component
function ErrorFallback({ error, resetErrorBoundary }) {
  // Log error to Sentry in production
  React.useEffect(() => {
    reportToSentry(error);
  }, [error]);

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <ErrorOutline color="error" sx={{ fontSize: 64 }} />
        </Box>
        
        <Typography variant="h4" gutterBottom>
          Oops! Something went wrong
        </Typography>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          We're sorry for the inconvenience. The error has been logged and we'll look into it.
        </Typography>
        
        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" component="pre" sx={{ textAlign: 'left' }}>
              {error.message}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={resetErrorBoundary}
          >
            Try Again
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => window.location.href = '/'}
          >
            Go Home
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

// Error boundary wrapper component
export function ErrorBoundary({ children }) {
  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
          console.error('Error boundary caught:', error, errorInfo);
        }
      }}
      onReset={() => {
        // Clear any error state if needed
        window.location.reload();
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}

// Route-level error boundary with different UI
export function RouteErrorBoundary({ children }) {
  return (
    <ReactErrorBoundary
      FallbackComponent={({ error, resetErrorBoundary }) => (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Failed to load this page
          </Typography>
          <Button size="small" onClick={resetErrorBoundary}>
            Retry
          </Button>
        </Box>
      )}
      onError={(error) => {
        reportToSentry(error);
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}

// Component-level error boundary for non-critical errors
export function ComponentErrorBoundary({ children, fallback }) {
  return (
    <ReactErrorBoundary
      FallbackComponent={fallback || (() => (
        <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">
            Unable to load this component
          </Typography>
        </Box>
      ))}
      onError={(error) => {
        console.warn('Component error:', error);
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}